import { SWRConfig } from 'swr'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Filter, X } from 'lucide-react'
import GlobalFilters from '@/components/dashboard/GlobalFilters'
import PrefectureSummaryCards from '@/components/prefecture-dashboard/PrefectureSummaryCards'
import PrefectureGenerationStats from '@/components/prefecture-dashboard/PrefectureGenerationStats'
import InterPrefectureAnalysis from '@/components/prefecture-dashboard/InterPrefectureAnalysis'
import PrefectureRanking from '@/components/prefecture-dashboard/PrefectureRanking'
import ChampionshipHistory from '@/components/prefecture-dashboard/ChampionshipHistory'
import RecentPerformance from '@/components/prefecture-dashboard/RecentPerformance'
import ActiveFiltersBar from '@/components/dashboard/ActiveFiltersBar'
import YouTubeBanner from '@/components/ui/YouTubeBanner'

import useFilterStore from '@/store/useFilterStore'
import { useFilterOptions } from '@/hooks/useFilterOptions'
import { useURLParams } from '@/hooks/useURLParams'

const PrefectureDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { selectedPrefectures } = useFilterStore()
  const { data: filterOptions } = useFilterOptions()
  
  // URLパラメータからフィルターを自動設定（YouTube投稿などからの流入時）
  useURLParams()

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const closeSidebar = () => {
    setIsSidebarOpen(false)
  }

  // 選択された都道府県の名前を取得
  const selectedPrefectureNames = selectedPrefectures.length > 0
    ? filterOptions?.prefectures
        ?.filter(p => selectedPrefectures.includes(p.id))
        ?.map(p => p.name)
        ?.join('、')
    : null

  // タイトルを動的に生成
  const getTitle = () => {
    if (selectedPrefectureNames) {
      return `${selectedPrefectureNames}の都道府県別分析`
    }
    return '都道府県別ダッシュボード'
  }

  // メタディスクリプションを動的に生成
  const getDescription = () => {
    if (selectedPrefectureNames) {
      return `${selectedPrefectureNames}のサッカー競技力分析、他県との対戦成績、世代別統計をお届けします`
    }
    return '都道府県別のサッカー競技力分析、対戦成績、世代別統計をお届けします'
  }

  // BreadcrumbList構造化データ生成
  const getBreadcrumbData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": "https://niche-football.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "都道府県別ダッシュボード",
          "item": "https://niche-football.com/prefecture-dashboard"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": selectedPrefectureNames || "都道府県別分析",
          "item": "https://niche-football.com/prefecture-dashboard"
        }
      ]
    }
  }

  return (
    <>
      <Helmet>
        <title>{getTitle()}</title>
        <meta name="description" content={getDescription()} />
        <meta property="og:title" content={getTitle()} />
        <meta property="og:description" content={getDescription()} />
        <meta property="twitter:title" content={getTitle()} />
        <meta property="twitter:description" content={getDescription()} />
        
        {/* BreadcrumbList構造化データ */}
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbData())}
        </script>
      </Helmet>
      <SWRConfig
        value={{
          fetcher: (url: string) => fetch(url).then((res) => res.json()),
          revalidateOnFocus: false,
        }}
      >
        <div className="min-h-screen bg-gray-50">
          <div className="flex">
            {/* デスクトップサイドバー */}
            <div className="hidden lg:block w-80 bg-white shadow-lg min-h-screen">
              <GlobalFilters />
            </div>
            
            {/* メインコンテンツ */}
            <div className="flex-1">
              {/* ヘッダー */}
              <div className="bg-white shadow-sm border-b px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={toggleSidebar}
                      className="lg:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                    >
                      <Filter className="h-5 w-5" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {getTitle()}
                    </h1>
                  </div>
                </div>
              </div>

              {/* コンテンツエリア */}
              <div className="p-6">
                {/* アクティブフィルターバー */}
                <div className="mb-6">
                  <ActiveFiltersBar />
                </div>
                
                {/* YouTubeバナー（YouTubeからの流入時） */}
                <div className="mb-6">
                  <YouTubeBanner />
                </div>
                
                {/* サマリーカード */}
                <PrefectureSummaryCards />
                
                {/* 世代別統計 */}
                <div className="mt-8">
                  <PrefectureGenerationStats />
                </div>
                
                {/* 都道府県間対戦分析 */}
                <div className="mt-8">
                  <InterPrefectureAnalysis />
                </div>
                
                {/* 都道府県ランキング */}
                <div className="mt-8">
                  <PrefectureRanking />
                </div>
                
                
                
                {/* 優勝履歴 */}
                <div className="mt-8">
                  <ChampionshipHistory />
                </div>
                
                {/* 最近の成績 */}
                <div className="mt-8">
                  <RecentPerformance />
                </div>
              </div>
            </div>
          </div>

          {/* モバイルサイドバー */}
          {isSidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50">
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={closeSidebar} />
              <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-lg">
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">フィルター</h2>
                  <button
                    onClick={closeSidebar}
                    className="p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4">
                  <GlobalFilters />
                </div>
              </div>
            </div>
          )}
        </div>
      </SWRConfig>
    </>
  )
}

export default PrefectureDashboard
